import { nanoid } from 'nanoid';
import { isPresent, Nullish } from './typescript';

/**
 * Returns a promise that resolves after given delay.
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => { setTimeout(resolve, ms); });
}

export function mergeInto<T extends { id: any }>(item: T, list: Array<T>): void;
export function mergeInto<T>(item: T, list: Array<T>, predicate: (item: T) => boolean): void;
export function mergeInto(item: any, list: Array<any>, predicate = (i: any) => i.id === item.id): void {
  const index = list.findIndex(predicate);
  if (index >= 0) {
    list.splice(index, 1, item);
  } else {
    list.push(item);
  }
}

export function mergeLists<T extends { id: any }>(source: Array<T>, target: Array<T>): void {
  source.forEach(item => {
    mergeInto(item, target);
  });
}

export function getById<ID, T extends { id: ID | null }>(list: Array<T>, id: ID): T | undefined {
  return list.find(i => i.id === id);
}

export function extractIds<T extends { id: any }>(list: Nullish<Array<T>>): Array<Exclude<T['id'], null | undefined>> {
  return list?.map(i => i.id).filter(isPresent) ?? [];
}

export function updateById<ID, T extends { id: ID | null }>(list: Array<T>, id: ID, update: (item: T) => void): void {
  const item = list.find(i => i.id === id);
  if (item) update(item);
}

export function removeById<ID, T extends { id: ID | null }>(list: Array<T>, id: ID): void {
  removeBy(list, i => i.id === id);
}

export function removeBy<T>(list: Array<T>, predicate: (item: T) => boolean): void {
  const index = list.findIndex(predicate);
  if (index >= 0) {
    list.splice(index, 1);
  }
}

export function toggleArrayItem<T>(list: Array<T>, item: T): void {
  const index = list.indexOf(item);
  if (index >= 0) {
    list.splice(index, 1);
  } else {
    list.push(item);
  }
}

export interface TitleAndBody {
  title: string;
  body: string | null;
}

export function splitTitleAndBody(text: Nullish<string>): TitleAndBody {
  const [title, ...rest] = text?.split(/[\n\r]/).map(s => s.trim()).filter(Boolean) ?? [];
  return { title, body: rest.join('\n') || null };
}

export function uid<T extends string>(): T {
  return nanoid(10) as T;
}

/**
 * @deprecated prefer using native functions
 */
export function sumArray(value: Array<number> = []): number {
  return value.reduce((a, b) => a + b, 0);
}
